import React from 'react'
import { startCase } from 'lodash'
import classNames from 'classnames'

import { Text } from 'marketing-site/src/library/elements/Text'
import { CTATheme, mq } from 'marketing-site/src/library/utils'
import { CTATheme as CTAThemeMVP } from 'marketing-site/src/mvp/library/utils/constants/CTATheme'
import { HeaderNavigationLinkBadge } from './HeaderNavigationLinkBadge'
import { IHeaderNavigationMenuItem, getUrlWithPageviewParam } from '..'
import { Icon as CTALinkIcon, renderIconSVG } from 'marketing-site/src/library/utils/icons'
import { Icon as CTALinkIconMVP } from 'marketing-site/src/mvp/library/utils/icons'
import { CTALink } from 'marketing-site/src/library/elements/CTALink'
import { CTALink as CTALinkMVP } from 'marketing-site/src/mvp/library/elements/CTALink'
import { Icon } from 'marketing-site/src/mvp/library/elements/Icon'

export function HeaderNavigationMenuItem({
  contentType = 'flex',
  theme = 'light',
  title,
  subtitle,
  subhead,
  url,
  badge,
  icon,
  titleIcon,
  newWindow,
  cta,
}: IHeaderNavigationMenuItem): React.ReactElement {
  const titleClasses = classNames('header-navigation-menu-item__title', {
    'header-navigation-menu-item__title--underline': !titleIcon,
  })

  const classes = classNames('header-navigation-menu-item', {
    [`header-navigation-menu-item--theme-${theme}`]: theme,
    'no-subtitle': !subtitle,
  })

  return (
    <>
      <div className={classes} data-testid="header-navigation-menu-item">
        <>
          {contentType === 'flex' ? (
            <CTALink
              bgColor={theme === 'light' ? CTATheme.LinkOnlyBlack : CTATheme.LinkOnlyWhite}
              url={getUrlWithPageviewParam(url)}
              text={title}
              textSize={titleIcon === CTALinkIcon.Arrow ? 'body-bold' : 'body'}
              arrow={titleIcon === CTALinkIcon.Arrow}
              icon={titleIcon === CTALinkIcon.Arrow ? CTALinkIcon.Arrow : undefined}
              newWindow={newWindow}
            >
              <>
                {/* The following line checks against “undefined”
                since one of the possible values for icon is “0” */}
                {icon !== undefined && <Icon family={icon.family} icon={icon.icon} />}
                <div className="header-navigation-menu-item__content">
                  <div className={titleClasses}>
                    {titleIcon ? (
                      <>
                        <Text brand="default" size="body-bold">
                          {title}
                        </Text>
                        {renderIconSVG(CTALinkIcon.Arrow, 'cta-link__arrow')}
                      </>
                    ) : (
                      <Text brand="default" size="body">
                        {title}
                      </Text>
                    )}
                    {badge && (
                      <div className="header-navigation-menu-item__badge">
                        <HeaderNavigationLinkBadge {...badge} />
                      </div>
                    )}
                  </div>
                  {subhead ? (
                    <div className="header-navigation-menu-item__subhead">
                      <Text brand="default" size="caption">
                        {subhead}
                      </Text>
                    </div>
                  ) : (
                    subtitle && (
                      <div className="header-navigation-menu-item__subhead">
                        <Text brand="default" size="caption">
                          {subtitle}
                        </Text>
                      </div>
                    )
                  )}
                </div>
              </>
            </CTALink>
          ) : (
            <CTALinkMVP
              variant={theme === 'light' ? CTAThemeMVP.LinkOnlySlate : CTAThemeMVP.LinkOnlyPaper}
              url={getUrlWithPageviewParam(url)}
              text={title}
              textSize={titleIcon === CTALinkIconMVP.Arrow ? 'body-bold' : 'body'}
              icon={titleIcon === CTALinkIconMVP.Arrow ? CTALinkIconMVP.Arrow : undefined}
              newWindow={newWindow}
            >
              <>
                {/* The following line checks against “undefined”
                since one of the possible values for icon is “0” */}
                {icon !== undefined && <Icon family={icon.family} icon={icon.icon} />}
                <div className="header-navigation-menu-item__content">
                  <div className={titleClasses}>
                    {!!titleIcon ? (
                      <>
                        <Text brand="default" size="body-bold">
                          {title}
                        </Text>
                        <Icon icon="arrow-link" />
                      </>
                    ) : (
                      <Text brand="default" size="body">
                        {title}
                      </Text>
                    )}
                    {!!badge && (
                      <div className="header-navigation-menu-item__badge">
                        <HeaderNavigationLinkBadge {...badge} />
                      </div>
                    )}
                  </div>
                  {!!subhead ? (
                    <div className="header-navigation-menu-item__subhead">
                      <Text brand="default" size="caption">
                        {subhead}
                      </Text>
                    </div>
                  ) : (
                    subtitle && (
                      <div className="header-navigation-menu-item__subhead">
                        <Text brand="default" size="caption">
                          {subtitle}
                        </Text>
                      </div>
                    )
                  )}
                </div>
              </>
            </CTALinkMVP>
          )}

          {!!cta && (
            <div className="header-navigation-menu-item__cta">
              {contentType === 'flex' ? (
                <CTALink
                  bgColor={theme === 'light' ? CTATheme.LinkOnlyBlue : CTATheme.LinkOnlyWhite}
                  url={cta.url}
                  text={cta.text}
                  arrow={true}
                  icon={startCase(cta.icon) === CTALinkIcon.Arrow ? CTALinkIcon.Arrow : undefined}
                  newWindow={cta.newWindow}
                />
              ) : (
                <CTALinkMVP
                  {...cta}
                  variant={theme === 'light' ? CTAThemeMVP.LinkOnlyBlue : CTAThemeMVP.LinkOnlyPaper}
                />
              )}
            </div>
          )}
        </>
      </div>

      <style jsx>
        {`
          .header-navigation-menu-item {
            display: flex;
            flex-direction: column;
            align-items: start;

            & > :global(.cta-link) {
              display: flex;
              gap: 1rem;
              justify-content: start;
              align-items: start;

              &:focus,
              &:hover {
                text-decoration: none;
              }
            }

            // Remove bottom border in Grid variant.
            & > :global(.cta-link[class*='link-only'] span) {
              border-bottom: none;
            }

            &__content {
              display: flex;
              flex-direction: column;
              align-items: start;
            }

            &__title {
              display: flex;
              gap: 0.5rem;
              align-items: center;
              margin-top: 0.125rem;
            }

            &__subhead {
              margin-top: 0.125rem;

              :global(a) {
                color: inherit;
                text-decoration: underline;
              }
            }

            &__cta {
              margin-top: 0.75rem;
              margin-left: 3rem;
            }

            :global(.cta-link > .icon) {
              flex-shrink: 0;
            }

            &--theme-light {
              color: ${contentType === 'flex' ? 'var(--slate)' : 'var(--mvp-slate)'};

              .header-navigation-menu-item__subhead {
                color: var(--neutral-700);
              }
            }

            &--theme-dark,
            &--theme-resolution-bot {
              color: var(--paper);

              .header-navigation-menu-item__subhead {
                color: var(--neutral-200);
              }
            }
          }

          @media (${mq.desktop}) {
            .header-navigation-menu-item {
              &__badge {
                display: none;
              }
            }
          }
        `}
      </style>
    </>
  )
}
